<template>
  <div
    class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50"
  >
    <!-- Back Button -->
    <div class="absolute top-4 left-4 sm:top-6 sm:left-6">
      <button
        type="button"
        @click="handleBackNavigation"
        :disabled="loading"
        class="flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeftIcon class="h-4 w-4 mr-2" aria-hidden="true" />
        {{ t("common.back") }}
      </button>
    </div>

    <!-- Header -->
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="flex justify-center">
        <div class="flex items-center space-x-3">
          <LockClosedIcon class="h-10 w-10 text-blue-600" />
          <ArrowRightIcon class="h-6 w-6 text-gray-400" />
          <KeyIcon class="h-10 w-10 text-green-600" />
        </div>
      </div>
      <h2
        class="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900"
      >
        {{ t("auth.changePassword") }}
      </h2>
      <p class="mt-2 text-center text-sm/6 text-gray-600">
        {{ t("auth.changePasswordSubtitle") }}
      </p>
    </div>

    <!-- Main Form -->
    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
        <!-- Password Change Form -->
        <form
          @submit.prevent="onFinish(formState)"
          class="space-y-6"
          method="post"
          action="#"
        >
          <!-- 当前密码输入框 -->
          <div>
            <label
              for="current-password"
              class="block text-sm/6 font-medium text-gray-900"
            >
              {{ t("auth.currentPassword") }}
            </label>
            <div class="mt-2 relative">
              <input
                id="current-password"
                v-model="formState.oldPassword"
                :type="showCurrentPassword ? 'text' : 'password'"
                name="current-password"
                autocomplete="current-password"
                required
                :disabled="loading"
                class="block w-full rounded-md bg-white px-3 py-1.5 pr-10 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                :placeholder="t('auth.enterCurrentPassword')"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                <button
                  type="button"
                  @click="toggleCurrentPasswordVisibility"
                  tabindex="-1"
                  class="text-gray-400 hover:text-gray-500 focus:outline-none"
                  :aria-label="
                    showCurrentPassword
                      ? t('form.hidePassword')
                      : t('form.showPassword')
                  "
                >
                  <EyeIcon
                    v-if="showCurrentPassword"
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                  <EyeSlashIcon v-else class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <!-- 新密码输入框 -->
          <div>
            <label
              for="new-password"
              class="block text-sm/6 font-medium text-gray-900"
            >
              {{ t("auth.newPassword") }}
            </label>
            <div class="mt-2 relative">
              <input
                id="new-password"
                v-model="formState.newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                name="new-password"
                autocomplete="new-password"
                required
                :disabled="loading"
                minlength="8"
                class="block w-full rounded-md bg-white px-3 py-1.5 pr-10 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                :placeholder="t('form.enterPassword')"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                <button
                  type="button"
                  @click="toggleNewPasswordVisibility"
                  tabindex="-1"
                  class="text-gray-400 hover:text-gray-500 focus:outline-none"
                  :aria-label="
                    showNewPassword
                      ? t('form.hidePassword')
                      : t('form.showPassword')
                  "
                >
                  <EyeIcon
                    v-if="showNewPassword"
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                  <EyeSlashIcon v-else class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <!-- 密码要求提示 -->
          <password-requirements
            v-if="formState.newPassword"
            :password="formState.newPassword"
            class="mt-2"
          />

          <!-- 确认密码输入框 -->
          <div>
            <label
              for="confirm-password"
              class="block text-sm/6 font-medium text-gray-900"
            >
              {{ t("auth.confirmPassword") }}
            </label>
            <div class="mt-2 relative">
              <input
                id="confirm-password"
                v-model="formState.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                name="confirm-password"
                autocomplete="new-password"
                required
                :disabled="loading"
                class="block w-full rounded-md bg-white px-3 py-1.5 pr-10 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                :placeholder="t('auth.enterConfirmPassword')"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                <button
                  type="button"
                  @click="toggleConfirmPasswordVisibility"
                  tabindex="-1"
                  class="text-gray-400 hover:text-gray-500 focus:outline-none"
                  :aria-label="
                    showConfirmPassword
                      ? t('form.hidePassword')
                      : t('form.showPassword')
                  "
                >
                  <EyeIcon
                    v-if="showConfirmPassword"
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                  <EyeSlashIcon v-else class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div>
            <button
              type="submit"
              :disabled="loading || !isFormValid"
              class="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <a-spin v-if="loading" class="mr-2" size="small" />
              {{ loading ? t("auth.changing") : t("auth.submit") }}
            </button>
          </div>
        </form>

        <!-- Divider -->
        <div class="relative mt-10">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-200" />
          </div>
          <div class="relative flex justify-center text-sm/6 font-medium">
            <span class="bg-white px-6 text-gray-900">{{
              t("auth.passwordTips")
            }}</span>
          </div>
        </div>

        <!-- Help Information -->
        <div class="mt-6">
          <div class="p-4 bg-gray-50 rounded-lg">
            <div class="text-xs text-gray-600 leading-relaxed space-y-4">
              <!-- 密码安全说明 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("auth.passwordTip1") }}
                </h4>
                <p>{{ t("auth.passwordTip1Desc") }}</p>
              </div>

              <!-- 安全保护 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("auth.passwordTip2") }}
                </h4>
                <p>{{ t("auth.passwordTip2Desc") }}</p>
              </div>

              <!-- 密码管理 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("auth.passwordTip3") }}
                </h4>
                <p>{{ t("auth.passwordTip3Desc") }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Cancel Button -->
        <div class="mt-6">
          <button
            type="button"
            @click="handleCancel"
            :disabled="loading"
            class="flex w-full justify-center rounded-md bg-gray-200 px-3 py-1.5 text-sm/6 font-semibold text-gray-700 shadow-xs hover:bg-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ t("auth.cancel") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import * as Sentry from "@sentry/vue";
import {
  LockClosedIcon,
  KeyIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
} from "@heroicons/vue/24/outline";
import { useAuthStore } from "@/stores/auth";
import PasswordRequirements from "@/components/form/PasswordRequirements.vue";
import { notificationService } from "@/services/notificationService";

const router = useRouter();
const { t } = useI18n();
const authStore = useAuthStore();
const loading = ref(false);

// 表单状态
const formState = reactive({
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
});

// 密码可见性控制
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

// 密码可见性切换函数
function toggleCurrentPasswordVisibility() {
  showCurrentPassword.value = !showCurrentPassword.value;
}

function toggleNewPasswordVisibility() {
  showNewPassword.value = !showNewPassword.value;
}

function toggleConfirmPasswordVisibility() {
  showConfirmPassword.value = !showConfirmPassword.value;
}

// 表单验证
const isFormValid = computed(() => {
  return (
    formState.oldPassword.length > 0 &&
    formState.newPassword.length >= 8 &&
    /[a-zA-Z]/.test(formState.newPassword) &&
    /\d/.test(formState.newPassword) &&
    formState.confirmPassword === formState.newPassword
  );
});

// 处理表单提交
async function onFinish(values: any) {
  if (values.newPassword !== values.confirmPassword) {
    notificationService.error(
      "auth.passwordChangeError",
      t("auth.passwordMismatch"),
    );
    return;
  }

  loading.value = true;
  try {
    await authStore.handlePasswordChange(
      values.oldPassword,
      values.newPassword,
    );
    notificationService.success("auth.passwordChangeSuccess");
    handleSuccessRedirect();
  } catch (error: any) {
    console.error("Password change error:", error);

    // 只有非 401 错误才上报到 Sentry
    if (error.response?.status !== 401) {
      Sentry.captureException(error, {
        tags: {
          component: "ModifyPasswordView",
          action: "changePassword",
        },
        extra: {
          responseStatus: error.response?.status,
          responseData: error.response?.data,
        },
      });
    }

    let errorMsg;
    if (error.response?.status === 400) {
      errorMsg = t("auth.invalidPassword");
    } else if (error.response?.status === 401) {
      errorMsg = t("auth.wrongPassword");
    } else {
      errorMsg = t("common.systemError");
    }
    notificationService.error("auth.passwordChangeError", errorMsg);
  } finally {
    loading.value = false;
  }
}

// 处理成功后的重定向
async function handleSuccessRedirect() {
  try {
    await router.push("/login");
  } catch (error) {
    console.error("Redirect error:", error);
    Sentry.captureException(error, {
      tags: {
        component: "ModifyPasswordView",
        action: "redirectAfterChange",
      },
      extra: {
        targetPath: "/login",
        currentPath: window.location.pathname,
      },
    });
    window.location.href = "/login";
  }
}

// 取消修改
function handleCancel() {
  try {
    // 智能导航：优先使用 history 返回，失败则导航到安全页面
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push("/authorize");
    }
  } catch (error) {
    console.error("Cancel redirect error:", error);
    Sentry.captureException(error, {
      tags: {
        component: "ModifyPasswordView",
        action: "cancelRedirect",
      },
    });
    router.push("/authorize");
  }
}

// 新的返回导航处理
function handleBackNavigation() {
  try {
    // 直接导航到授权页面，避免 router.back() 的问题
    router.push("/authorize");
  } catch (error) {
    console.error("Back navigation error:", error);
    // 回退到授权页面
    router.push("/authorize");
  }
}
</script>
